import type {
  RegistryApproval,
  RegistryApprovalStatus,
  RegistryDecision,
  RegistryActivityPage,
  RegistryMe,
  RegistryStaffMember,
  RegistryUploadTarget,
} from './registryApiTypes';
import type {
  RegistryAllocation,
  RegistryEntityDetail,
  RegistryEntitySummary,
  RegistryFundOpportunityAllocations,
} from './registryEntityTypes';
import type {
  DealValues,
  RegistryDealSetup,
  RegistryFund,
  RegistryHouseDefaults,
  RegistryOffer,
  RegistrySetupOpportunity,
  RegistryUnitPrice,
} from './registryDealTypes';

export class RegistryApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'RegistryApiError';
  }
}

export type TokenGetter = () => Promise<string>;

/** Calls the admin API's /registry routes through the same /api proxy the admin portal uses. */
export class RegistryApi {
  /** `onRefused` runs when the server refuses a request (403 or 404), so the console can re-read its permissions. */
  constructor(
    private readonly getToken: TokenGetter,
    private readonly onRefused?: () => void,
  ) {}

  getMe(): Promise<RegistryMe> {
    return this.request<RegistryMe>('GET', 'registry/me');
  }

  listStaff(): Promise<RegistryStaffMember[]> {
    return this.request<RegistryStaffMember[]>('GET', 'registry/staff');
  }

  listApprovals(status: RegistryApprovalStatus): Promise<RegistryApproval[]> {
    return this.request<RegistryApproval[]>(
      'GET',
      `registry/approvals?status=${encodeURIComponent(status)}`,
    );
  }

  decideApproval(
    approvalId: string,
    decision: RegistryDecision,
    comment?: string,
  ): Promise<RegistryApproval> {
    return this.request<RegistryApproval>(
      'POST',
      `registry/approvals/${encodeURIComponent(approvalId)}/decision`,
      { decision, ...(comment ? { comment } : {}) },
    );
  }

  createTestApproval(title: string, amount?: string): Promise<RegistryApproval> {
    return this.request<RegistryApproval>('POST', 'registry/approvals/test-requests', {
      title,
      ...(amount ? { amount } : {}),
    });
  }

  listEntities(): Promise<RegistryEntitySummary[]> {
    return this.request<RegistryEntitySummary[]>('GET', 'registry/entities');
  }

  getEntity(entityId: string): Promise<RegistryEntityDetail> {
    return this.request<RegistryEntityDetail>(
      'GET',
      `registry/entities/${encodeURIComponent(entityId)}`,
    );
  }

  listFundOpportunities(): Promise<RegistryFundOpportunityAllocations[]> {
    return this.request<RegistryFundOpportunityAllocations[]>('GET', 'registry/fund-opportunities');
  }

  listFundOpportunityAllocations(fundOpportunityId: string): Promise<RegistryAllocation[]> {
    return this.request<RegistryAllocation[]>(
      'GET',
      `registry/fund-opportunities/${encodeURIComponent(fundOpportunityId)}/allocations`,
    );
  }

  getDefaults(): Promise<RegistryHouseDefaults> {
    return this.request('GET', 'registry/defaults');
  }

  updateDefaults(values: DealValues): Promise<RegistryHouseDefaults> {
    return this.request('PATCH', 'registry/defaults', { values });
  }

  uploadLogo(file: File): Promise<RegistryHouseDefaults> {
    return this.upload('registry/defaults/logo', file, {}, 'Logo upload failed');
  }

  /** This fund's own logo; the default logo is unchanged. */
  uploadSetupLogo(setupId: string, file: File): Promise<RegistryDealSetup> {
    return this.upload(
      `registry/setups/${encodeURIComponent(setupId)}/logo`,
      file,
      {},
      'Logo upload failed',
    );
  }

  uploadOfferDocument(setupId: string, title: string, file: File): Promise<RegistryDealSetup> {
    return this.upload(
      `registry/setups/${encodeURIComponent(setupId)}/documents`,
      file,
      { title },
      'Document upload failed',
    );
  }

  addOfferLink(setupId: string, title: string, url: string): Promise<RegistryDealSetup> {
    return this.request('POST', `registry/setups/${encodeURIComponent(setupId)}/document-links`, {
      title,
      url,
    });
  }

  renameOfferDocument(
    setupId: string,
    documentId: string,
    title: string,
  ): Promise<RegistryDealSetup> {
    return this.request(
      'PATCH',
      `registry/setups/${encodeURIComponent(setupId)}/documents/${encodeURIComponent(documentId)}`,
      { title },
    );
  }

  removeOfferDocument(setupId: string, documentId: string): Promise<RegistryDealSetup> {
    return this.request(
      'DELETE',
      `registry/setups/${encodeURIComponent(setupId)}/documents/${encodeURIComponent(documentId)}`,
    );
  }

  /** Puts the offer's documents in this order; it lists every one of them. */
  reorderOfferDocuments(setupId: string, documentIds: string[]): Promise<RegistryDealSetup> {
    return this.request('POST', `registry/setups/${encodeURIComponent(setupId)}/document-order`, {
      documentIds,
    });
  }

  /** The registry's audit trail, newest first. */
  listActivity(page = 1, limit = 50): Promise<RegistryActivityPage> {
    return this.request('GET', `registry/activity?page=${page}&limit=${limit}`);
  }

  /**
   * Files go straight to storage through a signed URL, then `path` is called with the upload's
   * id. A multipart POST through the load balancer is refused by its firewall.
   */
  private async upload<T>(
    path: string,
    file: File,
    fields: Record<string, string>,
    failure: string,
  ): Promise<T> {
    const target = await this.request<RegistryUploadTarget>('POST', 'registry/uploads', {});
    const stored = await fetch(target.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': target.contentType },
      body: file,
    }).catch(() => null);
    if (!stored?.ok) {
      throw new RegistryApiError(
        `${failure} (storage ${stored ? `HTTP ${stored.status}` : 'unreachable'})`,
        stored?.status ?? 0,
      );
    }
    return this.request('POST', path, { ...fields, uploadId: target.uploadId });
  }

  listSetupOpportunities(): Promise<RegistrySetupOpportunity[]> {
    return this.request('GET', 'registry/setup-opportunities');
  }

  listSetups(): Promise<RegistryDealSetup[]> {
    return this.request('GET', 'registry/setups');
  }

  getSetup(id: string): Promise<RegistryDealSetup> {
    return this.request('GET', `registry/setups/${encodeURIComponent(id)}`);
  }

  startSetup(fundOpportunityExternalId: string): Promise<RegistryDealSetup> {
    return this.request('POST', 'registry/setups', { fundOpportunityExternalId });
  }

  updateSetup(id: string, values: DealValues, completedStep?: string): Promise<RegistryDealSetup> {
    return this.request('PATCH', `registry/setups/${encodeURIComponent(id)}`, {
      values,
      completedStep,
    });
  }

  /** Makes a staff member the account contact; their details become the reset values too. */
  chooseAccountContact(id: string, staffExternalId: string): Promise<RegistryDealSetup> {
    return this.request('PATCH', `registry/setups/${encodeURIComponent(id)}`, {
      accountContact: staffExternalId,
    });
  }

  activateSetup(id: string): Promise<RegistryDealSetup> {
    return this.request('POST', `registry/setups/${encodeURIComponent(id)}/activate`);
  }

  requestOfferPublish(id: string): Promise<RegistryDealSetup> {
    return this.request('POST', `registry/setups/${encodeURIComponent(id)}/publishRequests`);
  }

  requestBankVerification(id: string): Promise<RegistryDealSetup> {
    return this.request(
      'POST',
      `registry/setups/${encodeURIComponent(id)}/bankVerificationRequests`,
    );
  }

  listFunds(): Promise<RegistryFund[]> {
    return this.request('GET', 'registry/funds');
  }

  getFund(id: string): Promise<RegistryFund> {
    return this.request('GET', `registry/funds/${encodeURIComponent(id)}`);
  }

  listPrices(classId: string): Promise<RegistryUnitPrice[]> {
    return this.request('GET', `registry/classes/${encodeURIComponent(classId)}/prices`);
  }

  addPrice(
    classId: string,
    price: { effectiveDate: string; entry: string; mid: string; exit: string },
  ): Promise<RegistryUnitPrice[]> {
    return this.request('POST', `registry/classes/${encodeURIComponent(classId)}/prices`, price);
  }

  importPrices(classId: string, csv: string): Promise<RegistryUnitPrice[]> {
    return this.request('POST', `registry/classes/${encodeURIComponent(classId)}/prices/import`, {
      csv,
    });
  }

  listOffers(): Promise<RegistryOffer[]> {
    return this.request('GET', 'registry/offers');
  }

  getOffer(id: string): Promise<RegistryOffer> {
    return this.request('GET', `registry/offers/${encodeURIComponent(id)}`);
  }

  /** A refusal may mean permissions changed since sign-in; re-reading them can't itself be one. */
  private reportRefusal(status: number, path: string) {
    if ((status === 403 || status === 404) && path !== 'registry/me') this.onRefused?.();
  }

  private async request<T>(
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
  ): Promise<T> {
    const token = await this.getToken();
    const response = await fetch(`/api/${path}`, {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      this.reportRefusal(response.status, path);
      const message =
        typeof json?.error === 'string' && json.error
          ? json.error
          : `Request failed (${response.status})`;
      throw new RegistryApiError(message, response.status, json?.code, json?.details?.fields);
    }
    return json.data as T;
  }
}

export function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong. Try again.';
}
